import {
  asCampaignStatus,
  asCount,
  asIso,
  asPausedFrom,
  asRecipientStatus,
  asSendMode,
  asText,
  mapAudienceSnapshot,
  mapRecipientsPage,
  type ApiApproveBase,
  type ApiCampaignBase,
  type ApiRecipientBase,
  type ApiRecipientsPage,
} from '@/features/marketing-shared';
import type { SmsAccountNumber, SmsCampaign, SmsDraftInput, SmsRecipient } from '../model/types';

/**
 * API → tela e tela → API do SMS marketing (`campaignDto` com o bloco `sms`,
 * `sendersFor`, `parseApprove` em `legacy/fixe-api/services/marketingCampaign.js`).
 */

export interface ApiSmsMetrics {
  sent: number;
  delivered: number;
  rejected: number;
  unsubscribed: number;
}

export interface ApiSmsRecipient extends ApiRecipientBase {
  phoneMasked?: string;
}

export interface ApiSmsCampaign extends ApiCampaignBase {
  from?: string;
  text?: string;
  metrics?: ApiSmsMetrics;
  recipients?: ApiRecipientsPage<ApiSmsRecipient>;
}

/** `MktProvider.Sender`: número (ou Messaging Service) da conta liberado para a loja. */
export interface ApiSmsSender {
  id: string;
  telephoneNumber: string;
  paused: boolean;
}

export interface ApiSmsApproveBody extends ApiApproveBase {
  audience: { audienceId: string };
  from: string;
  text: string;
}

function mapSmsMetrics(metrics: ApiSmsMetrics | undefined): SmsCampaign['metrics'] {
  if (!metrics || typeof metrics !== 'object') return undefined;
  return {
    sent: asCount(metrics.sent),
    delivered: asCount(metrics.delivered),
    rejected: asCount(metrics.rejected),
    unsubscribed: asCount(metrics.unsubscribed),
  };
}

export function mapSmsRecipient(row: ApiSmsRecipient): SmsRecipient {
  return {
    id: asText(row.id),
    name: asText(row.name),
    phoneMasked: asText(row.phoneMasked),
    status: asRecipientStatus(row.status),
    reason: asText(row.reason),
    at: asText(row.at),
  };
}

export function mapSmsCampaign(dto: ApiSmsCampaign): SmsCampaign {
  return {
    id: asText(dto.id),
    name: asText(dto.name),
    from: asText(dto.from),
    text: asText(dto.text),
    status: asCampaignStatus(dto.status),
    audience: mapAudienceSnapshot(dto.audience),
    scheduledAt: asIso(dto.scheduledAt),
    sendMode: asSendMode(dto.sendMode),
    createdAt: asIso(dto.createdAt) ?? '',
    pausedFrom: asPausedFrom(dto.pausedFrom),
    failureSummary: asText(dto.failureSummary) || undefined,
    metrics: mapSmsMetrics(dto.metrics),
    recipients: mapRecipientsPage(dto.recipients, mapSmsRecipient),
  };
}

export function mapSmsSender(dto: ApiSmsSender): SmsAccountNumber {
  return {
    id: asText(dto.id) || asText(dto.telephoneNumber),
    telephoneNumber: asText(dto.telephoneNumber),
    paused: dto.paused === true,
  };
}

/** Corpo de `POST /sms-marketing/:idPassbook/campaigns/approve` (`parseApprove`). */
export function toSmsApproveBody(input: SmsDraftInput, allowPartial: boolean): ApiSmsApproveBody {
  const audienceId = input.audience.audienceId;
  return {
    ...(input.id ? { id: input.id } : {}),
    name: input.name.trim(),
    segmentationId: audienceId,
    audience: { audienceId },
    sendMode: input.sendMode,
    scheduledAt: input.sendMode === 'later' ? input.scheduledAt : null,
    allowPartial,
    from: input.from.trim(),
    // O texto vai como está: espaços e quebras fazem parte do SMS.
    text: input.text,
  };
}
